import {
  Customer,
  Database,
  DatabaseContentPage,
  DatabaseEntry,
  DatabaseField,
  DatabaseFieldGroup,
  DatabaseFieldType,
  DatabaseFieldVisibilityOperator,
  DatabaseTemplate,
  DatabaseVisibilitySource,
} from '~/api-client';

/**
 * Vergleichs-Funktionen pro Feld-Typ × Operator. Wird zentral hier
 * exportiert (statt aus util.ts), um den früheren Modul-Zyklus
 * `util.ts ↔ useVisibilityRules.ts` aufzulösen — der Zyklus war zwar
 * funktional unkritisch, hat aber im Production-Bundle in spezifischen
 * Konstellationen TDZ-Crashes ausgelöst.
 */
/**
 * EINE ZEILE DES ANZEIGEFILTERS IST EIN ODER.
 *
 * ═══ FESTGELEGT VOM BETREIBER AM 30.08.2026 ══════════════════════════════
 *
 *   Kategorie  ist gleich  Sprint1, Sprint2   → Sprint 1 ODER Sprint 2
 *
 *   Kategorie  ist gleich  Sprint1
 *   Kategorie  ist gleich  Sprint2            → BEIDES zugleich
 *
 * Das UND ueber mehrere Zeilen konnte `isVisible` schon immer (es faltet mit
 * `acc && val`). Das ODER innerhalb einer Zeile fehlte: Der Vergleich verlangte
 * beim Kategoriefeld `val2.length === 1`. Trug jemand zwei Werte ein, war die
 * Zeile NIE erfuellt — das Feld erschien nie, ohne jede Meldung.
 *
 * WAS SICH FUER BESTEHENDE DATENBANKEN AENDERT: Bei MEHRFACHAUSWAHL hiess
 * „ist gleich" bisher „genau diese Menge und nichts sonst". Jetzt heisst es
 * „enthaelt einen der genannten Werte". Der Betreiber hat das ausdruecklich
 * freigegeben — die alte Lesart laesst die Regel „eine Zeile = ODER" gar nicht
 * zu, denn eine Menge kann nicht gleichzeitig genau {A} und genau {A,B} sein.
 *
 * BEIDE SEITEN WERDEN ZU LISTEN GEMACHT: Die Regel speichert je nach
 * Oberflaeche eine Zeichenkette oder ein Feld davon, der Datensatz ebenso
 * (Einfach- gegen Mehrfachauswahl). Ohne diese Vereinheitlichung haengt das
 * Ergebnis davon ab, wie die Werte zufaellig abgelegt wurden.
 */
const alsListe = (wert: unknown): string[] => {
  if (wert === null || wert === undefined) return [];
  return (Array.isArray(wert) ? wert : [wert]).map((w) => String(w));
};

/** Trifft mindestens einer der Werte des Datensatzes auf die Zeile zu? */
const trifftZu = (imDatensatz: unknown, inDerZeile: unknown): boolean => {
  const zeile = alsListe(inDerZeile);
  if (zeile.length === 0) return false;
  return alsListe(imDatensatz).some((w) => zeile.includes(w));
};

export const COMPARISON_OPERATOR_MAP = new Map<DatabaseFieldType, Map<DatabaseFieldVisibilityOperator, (val1: unknown, val2: unknown) => boolean>>([
  [DatabaseFieldType.CreatedByField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => (val1 as Customer)?.id === (val2 as Customer)?.id],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => (val1 as Customer)?.id !== (val2 as Customer)?.id],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
  [DatabaseFieldType.CreatedDateField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => (val1 as Date) === (val2 as Date)],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => (val1 as Date) !== (val2 as Date)],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => (val1 as Date) < (val2 as Date)],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => (val1 as Date) > (val2 as Date)],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
  [DatabaseFieldType.ModifiedByField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => (val1 as Customer)?.id === (val2 as Customer)?.id],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => (val1 as Customer)?.id !== (val2 as Customer)?.id],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
  [DatabaseFieldType.ModifiedDateField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => (val1 as Date) === (val2 as Date)],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => (val1 as Date) !== (val2 as Date)],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => (val1 as Date) < (val2 as Date)],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => (val1 as Date) > (val2 as Date)],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
  [DatabaseFieldType.CalcField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
  [DatabaseFieldType.OwnerField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => (val1 as Customer)?.id === (val2 as Customer)?.id],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => (val1 as Customer)?.id !== (val2 as Customer)?.id],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
  [DatabaseFieldType.AgileField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.RatingField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.ImageField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.ChecklistField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined || (val1 as any[]).length > 0],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (val1 as any[]).length === 0],
  ])],
  [DatabaseFieldType.FileField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.DateField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => (val1 as Date) < (val2 as Date)],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => (val1 as Date) > (val2 as Date)],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.EmailField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => (val1 as string)?.includes(val2 as string)],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => (val1 as string)?.startsWith(val2 as string)],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined && (val1 as string).length > 0],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (val1 as string).length === 0],
  ])],
  [DatabaseFieldType.YesNoField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => (val1 === true) === (val2 === true)],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => (val1 === true) !== (val2 === true)],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 === true],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 !== true],
  ])],
  [DatabaseFieldType.CategoryField, new Map([
    /*
     * ══ „ENTHÄLT" TAT NICHTS (Befund 01.09.2026) ═══════════════════════════
     *
     * Hier stand `() => false`: Wer als Anzeigefilter „enthält" wählte, bekam
     * eine Regel, die NIE zutrifft — das Feld oder die Feldgruppe blieb für
     * immer unsichtbar, ohne Meldung. Aufgefallen ist es an den Bereichen der
     * Planungs-Maske: Die Gruppen „Business" und „IT" erschienen nie, obwohl
     * der Bereich angehakt war.
     *
     * Bei einer Mehrfachauswahl ist „enthält" die naheliegendste Lesart —
     * genau dieselbe Frage, die `EQUAL` über `trifftZu` beantwortet: Ist der
     * Wert der Zeile unter den gewählten? Beide dasselbe zu beantworten ist
     * kein Widerspruch: Bei einem Feld mit EINEM Wert fallen sie ohnehin
     * zusammen, und bei mehreren ist alles andere eine Falle.
     */
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => trifftZu(val1, val2)],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => trifftZu(val1, val2)],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => !trifftZu(val1, val2)],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined && (Array.isArray(val1) ? val1.length > 0 : !!val1)],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (Array.isArray(val1) ? val1.length === 0 : !val1)],
  ])],
  [DatabaseFieldType.LinkField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => (val1 as string)?.includes(val2 as string)],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => (val1 as string)?.startsWith(val2 as string)],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined && (val1 as string).length > 0],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (val1 as string).length === 0],
  ])],
  [DatabaseFieldType.NumberField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => (val1 as number) < (val2 as number)],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => (val1 as number) > (val2 as number)],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.PasswordField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined && (val1 as string)?.length > 0],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (val1 as string)?.length === 0],
  ])],
  [DatabaseFieldType.PercentageField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => (val1 as number) < (val2 as number)],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => (val1 as number) > (val2 as number)],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.TelnumberField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => (val1 as string)?.includes(val2 as string)],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => (val1 as string)?.startsWith(val2 as string)],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined && (val1 as string)?.length > 0],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (val1 as string)?.length === 0],
  ])],
  [DatabaseFieldType.TextField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => (val1 as string)?.includes(val2 as string)],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => (val1 as string)?.startsWith(val2 as string)],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined && (val1 as string)?.length > 0],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (val1 as string)?.length === 0],
  ])],
  [DatabaseFieldType.VideoField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.CurrencyField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => (val1 as number) < (val2 as number)],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => (val1 as number) > (val2 as number)],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.PeriodField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.CombinedTextField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
]);

/**
 * Reine Funktion zum Auswerten von Visibility-Rules. Lebt hier (statt
 * im Composable useVisibilityRules), damit auch Nicht-Composable-Code
 * (util.ts → CombinedTextField-Auswertung) sie nutzen kann, ohne den
 * früheren Modul-Zyklus zurückzubringen. useVisibilityRules.ts re-
 * exportiert sie für Backward-Compat.
 */
/**
 * REGEL UEBER DEN SEITENNAMEN (`source: CURRENT_PAGE`).
 *
 * Gebraucht fuer Bausteine im Kopf- und Fussbereich, die je Seite anders
 * aussehen muessen — bei der Minigolfkarte: auf der Startseite ist der Knopf
 * weg, im Aufbau heisst er „< Start", im Spiel „Neu starten". Ein Feldwert
 * kann das nicht beantworten, denn die offene Seite ist keine Eigenschaft
 * des Datensatzes.
 *
 * VERGLICHEN WIRD WIE BEI EINEM TEXTFELD, mit einer Ausnahme: die Zeile ist
 * ein ODER. Steht „ist gleich  Spiel, Ende", trifft sie auf beide Seiten zu.
 * Genau dieselbe Lesart wie oben beim Kategoriefeld.
 *
 * OHNE SEITENNAME BLEIBT DER BAUSTEIN SICHTBAR. Wer isVisible ohne vierten
 * Wert ruft (die Detailmaske, die Uebersicht, und vor allem der Filter der
 * SEITEN selbst), kann die Frage nicht beantworten. Ein „unsichtbar" waere
 * hier die schlechtere Wahl: der Baustein verschwaende, und niemand fuende
 * heraus warum. Beim Seitenfilter ist es ausserdem zwingend — die Liste der
 * sichtbaren Seiten bestimmt erst, WELCHE Seite offen ist; wuerde sie den
 * Seitennamen brauchen, drehte sich die Auswertung im Kreis.
 */
const trifftSeiteZu = (seitenname: string | undefined, rule: { operator?: DatabaseFieldVisibilityOperator, value?: unknown }): boolean => {
  if (seitenname === undefined) return true;
  const ist = String(seitenname);
  const soll = alsListe(rule.value);
  switch (rule.operator) {
    case DatabaseFieldVisibilityOperator.Equal:
      return soll.includes(ist);
    case DatabaseFieldVisibilityOperator.NotEqual:
      /* OHNE WERT SICHTBAR. Eine halb ausgefuellte Regel darf keinen Baustein
         verschwinden lassen — der Betreiber suchte den Fehler sonst am
         Baustein. Genauso haelt es der Feldvergleich: `val1 !== val2` ist bei
         leerem Wert wahr. */
      return !soll.includes(ist);
    case DatabaseFieldVisibilityOperator.Includes:
      return soll.some((s) => s !== '' && ist.includes(s));
    case DatabaseFieldVisibilityOperator.StartsWith:
      return soll.some((s) => s !== '' && ist.startsWith(s));
    case DatabaseFieldVisibilityOperator.NotEmpty:
      return ist !== '';
    case DatabaseFieldVisibilityOperator.Empty:
      return ist === '';
    default:
      return true;
  }
};

/**
 * REGEL UEBER DIE POSITION (`source: ERSTE_SEITE`).
 *
 * Betreiber 14.09.2026: „Dieser soll auf allen Seiten angezeigt werden ausser
 * auf der ersten Seite." Ueber den NAMEN ginge das auch — nur verliert die
 * Regel ihren Sinn, sobald die erste Seite umbenannt wird. Die Position
 * bleibt.
 *
 * `ist gleich` = nur auf der ersten Seite, `ist ungleich` = ueberall ausser
 * dort. Ohne Angabe (Listenansicht, Detailmaske) bleibt der Baustein
 * sichtbar — dieselbe Regel wie beim Seitennamen.
 */
const trifftErsteSeiteZu = (istErste: boolean | undefined,
                            rule: { operator?: DatabaseFieldVisibilityOperator }): boolean => {
  if (istErste === undefined) return true;
  switch (rule.operator) {
    case DatabaseFieldVisibilityOperator.Equal:
      return istErste;
    case DatabaseFieldVisibilityOperator.NotEqual:
      return !istErste;
    default:
      return true;
  }
};

export const isVisible = (obj: DatabaseField | DatabaseContentPage | DatabaseFieldGroup, database: Database | DatabaseTemplate, entry: DatabaseEntry | undefined, aktuelleSeite?: string, istErsteSeite?: boolean) => {
  if (!obj || !obj.visibilityRules || obj.visibilityRules.length === 0) {
    // always show if no visibility rules are defined
    return true;
  };
  return obj.visibilityRules.map((rule) => {
    const operator = rule.operator;
    if (!operator) {
      return true;
    }
    if (rule.source === DatabaseVisibilitySource.CurrentPage) {
      return trifftSeiteZu(aktuelleSeite, rule);
    }
    if (rule.source === DatabaseVisibilitySource.ErsteSeite) {
      return trifftErsteSeiteZu(istErsteSeite, rule);
    }
    const fieldRef = rule.fieldId;
    const compareField = database.fields?.find(f => f.id == fieldRef);
    if (!compareField) {
      return true;
    }
    const compareFn = COMPARISON_OPERATOR_MAP.get(compareField.fieldType as DatabaseFieldType)?.get(operator);
    if (!compareFn) {
      return true;
    }
    const compareValue = entry?.fieldsValues?.[compareField.id!];
    return compareFn(compareValue, rule.value);
  }).reduce((acc, val) => acc && val, true);
};
